import { Frame, Navigation, Select, TopBar } from "@shopify/polaris";
import {
  CalendarIcon,
  AdjustIcon,
  CashYenIcon,
  ChartLineIcon,
  ChartVerticalFilledIcon,
  ClipboardChecklistIcon,
  CreditCardIcon,
  DeliveryIcon,
  HomeIcon,
  ImportIcon,
  InventoryIcon,
  NotificationIcon,
  OrderIcon,
  PackageIcon,
  PhoneIcon,
  QuestionCircleIcon,
  ReceiptIcon,
  StoreIcon,
  TransferInternalIcon,
} from "@shopify/polaris-icons";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getMe } from "../api/me";
import { ADMIN_LOCALES, isAdminLocale } from "../lib/adminLocales";
import { localePreference, t, type LocalePreference } from "../lib/i18n";
import { LocaleChangeContext } from "../lib/LocaleChangeContext";
import { ProductLimitBanner } from "./ProductLimitBanner";
import { InventoryReconciliationNotice } from "./InventoryReconciliationNotice";
import { PurchaseOrderEmailReconciliationNotice } from "./PurchaseOrderEmailReconciliationNotice";

interface LayoutProps {
  children: ReactNode;
}

type NavIcon = typeof HomeIcon;

const COMPACT_NAVIGATION_QUERY = "(max-width: 767px)";

function compactNavigationMatches(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(COMPACT_NAVIGATION_QUERY).matches
  );
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const changeLocale = useContext(LocaleChangeContext);
  const selectedLocale = localePreference(window.location.search);
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [compactNavigation, setCompactNavigation] = useState(
    compactNavigationMatches,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(COMPACT_NAVIGATION_QUERY);
    const update = () => {
      setCompactNavigation(media.matches);
      if (!media.matches) setMobileNavOpen(false);
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const isActive = (path: string) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  const navItem = (label: string, path: string, icon: NavIcon) => ({
    label: t(label),
    icon,
    selected: isActive(path),
    onClick: () => {
      navigate(path);
      setMobileNavOpen(false);
    },
  });

  // A standalone contact destination is a Business benefit. Keep it hidden
  // until the current tenant's active plan has been resolved authoritatively;
  // contextual links to /contact can still provide email help on other plans.
  const showContactNavigation =
    meQuery.isSuccess &&
    meQuery.data.subscription.active === true &&
    meQuery.data.subscription.plan_code === "business";
  const dataAndSettingsItems = [
    navItem(t("CSV取り込み"), "/stocky-csv", ImportIcon),
    navItem(t("データ"), "/stocky-data", InventoryIcon),
    navItem(t("通知設定"), "/settings", NotificationIcon),
    navItem(t("プラン"), "/plans", CreditCardIcon),
    navItem(t("よくある質問"), "/help", QuestionCircleIcon),
    ...(showContactNavigation
      ? [navItem(t("お問い合わせ"), "/contact", PhoneIcon)]
      : []),
  ];

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      onNavigationToggle={() => setMobileNavOpen((v) => !v)}
    />
  );
  /* 毎日の業務動線（発注→倉庫→分析）の順にセクションを並べる。
     ラベル文言は全画面の見出し・営業資料・デモ動画の呼称と揃えて変えない。 */
  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={[
          navItem(t("ホーム"), "/", HomeIcon),
          navItem(t("ダッシュボード"), "/dashboard", ChartLineIcon),
        ]}
      />
      <Navigation.Section
        title={t("発注")}
        items={[
          navItem(t("注文書"), "/purchase-orders", OrderIcon),
          navItem(t("発注済み・入荷"), "/placed-orders", DeliveryIcon),
          navItem(t("発注点設定"), "/reorder-policies", InventoryIcon),
          navItem(t("季節性の商品別設定"), "/seasonal-rules", CalendarIcon),
          navItem(t("仕入先"), "/suppliers", DeliveryIcon),
          navItem(t("発注元情報"), "/company-profile", StoreIcon),
          navItem(t("仕入税率"), "/tax-rates", ReceiptIcon),
        ]}
      />
      <Navigation.Section
        title={t("倉庫")}
        items={[
          navItem(t("在庫調整"), "/inventory-adjustments", AdjustIcon),
          navItem(t("棚卸し"), "/stocktakes", ClipboardChecklistIcon),
          navItem(
            t("拠点間移動"),
            "/inventory-transfers",
            TransferInternalIcon,
          ),
          navItem(t("セット商品の在庫"), "/bom", PackageIcon),
        ]}
      />
      <Navigation.Section
        title={t("分析")}
        items={[
          navItem(
            t("在庫分析"),
            "/inventory-analytics",
            ChartVerticalFilledIcon,
          ),
          navItem(t("資金繰りカレンダー"), "/cashflow-calendar", CashYenIcon),
          navItem(t("レポート"), "/reports", ChartLineIcon),
        ]}
      />
      <Navigation.Section
        title={t("データ・設定")}
        separator
        items={dataAndSettingsItems}
      />
      <div style={{ padding: "1rem" }}>
        <Select
          label={t("表示言語")}
          options={[
            { label: t("自動（Shopify）"), value: "auto" },
            ...ADMIN_LOCALES.map(([value, label]) => ({ label, value })),
          ]}
          value={selectedLocale}
          onChange={(value) => {
            if (value === "auto" || isAdminLocale(value)) {
              changeLocale(value as LocalePreference);
              setMobileNavOpen(false);
            }
          }}
        />
      </div>
    </Navigation>
  );

  return (
    <Frame
      // The Polaris TopBar is otherwise an empty 56 px strip on desktop. Keep
      // its navigation control only where the sidebar collapses on a compact
      // viewport; desktop navigation starts at the top edge with no dead area.
      topBar={compactNavigation ? topBarMarkup : undefined}
      navigation={navigationMarkup}
      showMobileNavigation={mobileNavOpen}
      onNavigationDismiss={() => setMobileNavOpen(false)}
    >
      <InventoryReconciliationNotice />
      <PurchaseOrderEmailReconciliationNotice />
      <ProductLimitBanner />
      {children}
    </Frame>
  );
}
